'use client';
import { logger, LoggerEvents } from '@bw/core';
import { Button, ButtonProps } from '@bw/elements';
import { LoaderBlue } from '@bw/elements/icons';
import {
	FormResponse,
	StartAgencyContactFormData as StartAgencyContactFormApiData,
} from '@bw/external-apis';
import { gtmEvent } from '@bw/ui';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BaseForm } from '../base-form/BaseForm';
import { readHiddenInputs } from '../form-helpers';
import styles from './StartAnAgencyForm.module.scss';

const isRequiredErrorMessage = 'This field is required';

export type BecomeAnAgentForm = {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	zipCode: string;
	previousInsuranceExperience: string;
	cashAvailableForInvestment: string;
	captchaToken: string;
};

export type RequestState =
	| { status: 'ready' | 'loading' | 'success' }
	| {
			status: 'error';
			error: Error;
	  };

interface FormProps {
	formId: string;
	formData: {
		name: string;
		id?: string;
		labelText: string;
		inputType: string;
		placeholder?: string | null;
		selectOptions?: string[] | null;
		isRequired: boolean;
		validationRegex?: string;
		validationErrorMessage: string;
	}[];
	buttonData?: ButtonProps;
	formAction?: (
		formId: string,
		data: BecomeAnAgentForm
	) => Promise<FormResponse>;
}

const formProps = {
	formId: 'BecomeAnAgentForm',
	formData: [
		{
			name: 'firstName',
			labelText: 'First Name',
			inputType: 'text',
			placeholder: 'First Name',
			selectOptions: null,
			isRequired: true,
			validationErrorMessage: 'Please enter a valid first name',
		},
		{
			name: 'lastName',
			labelText: 'Last Name',
			inputType: 'text',
			placeholder: 'Last Name',
			selectOptions: null,
			isRequired: true,
			validationErrorMessage: 'Please enter a valid last name',
		},
		{
			name: 'phoneNumber',
			labelText: 'Phone Number',
			inputType: 'text',
			placeholder: 'Phone Number',
			selectOptions: null,
			isRequired: true,
			validationErrorMessage: 'Please enter a valid phone number',
		},
		{
			name: 'email',
			labelText: 'Email',
			inputType: 'email',
			placeholder: 'Email',
			selectOptions: null,
			isRequired: true,
			validationRegex:
				/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
					.source,
			validationErrorMessage: 'Please enter a valid email',
		},
		{
			name: 'zipCode',
			labelText: 'Zip Code',
			inputType: 'text',
			placeholder: 'Zip Code',
			selectOptions: null,
			isRequired: true,
			validationRegex: '^(?!00000)\\d{5}$', // Should it be this? '^[0-9]{5}(?:-[0-9]{4})?$',
			validationErrorMessage: 'Invalid zip code',
		},
		{
			name: 'previousInsuranceExperience',
			labelText: 'Do you have P&C insurance experience?',
			inputType: 'select',
			placeholder: 'Do you have P&C insurance experience?',
			selectOptions: ['Yes', 'No'],
			isRequired: true,
			validationErrorMessage:
				'Please let us know if you have P&C insurance experience',
		},
		{
			name: 'cashAvailableForInvestment',
			labelText: 'Available capital to invest',
			inputType: 'select',
			isRequired: true,
			placeholder: 'How much to you have available to invest?',
			selectOptions: ['$25k-$50k', '$50k-$100k', '$100k+'],
			validationErrorMessage: 'Please select a capital range',
		},
	],
} as const satisfies FormProps;

interface StartAnAgencyClientFormProps {
	formAction: (
		...args: StartAgencyContactFormApiData[]
	) => Promise<FormResponse>;
}

export function StartAnAgencyClientForm({
	formAction,
}: StartAnAgencyClientFormProps) {
	if (!formAction) {
		throw new Error('Form action is required');
	}

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<BecomeAnAgentForm>();
	const [requestState, setRequestState] = useState<RequestState>({
		status: 'ready',
	});
	const [hideForm, setHideForm] = useState<boolean>(false);
	const { executeRecaptcha } = useGoogleReCaptcha();

	const getCaptchaToken = async (
		action: string | undefined
	): Promise<string> => {
		const message =
			'executeRecaptcha not available on form submit, possible react script loading error.';
		if (!executeRecaptcha) {
			logger.error(LoggerEvents.GeneralErrorWebApp, { message });
			return 'no recaptcha1';
		}
		try {
			const token = await executeRecaptcha(action);
			return token;
		} catch (error) {
			logger.error(LoggerEvents.GeneralErrorWebApp, { message });
		}
		return 'no recaptcha1';
	};

	const onSubmit: SubmitHandler<StartAgencyContactFormApiData> = async (
		data,
		event
	) => {
		if (!event?.target) {
			return;
		}

		// hidden inputs don't trigger the onChange event, so we need to manually read them
		data = { ...data, ...readHiddenInputs(event.target as HTMLFormElement) };

		setRequestState({ status: 'loading' });
		setHideForm(true);

		try {
			const token = await getCaptchaToken(formProps.formId);
			data.captchaToken = token;

			const response = await formAction(data);

			if (typeof response.error === 'string') {
				throw new Error(response.error);
			}

			setRequestState({ status: 'success' });
			gtmEvent('form_success', { formName: formProps.formId });
			reset();
		} catch (error) {
			console.log('caught error');
			if (error instanceof Error) {
				setRequestState({ status: 'error', error });
				setHideForm(false);
			}
		}
	};

	const ErrorDisplay = ({ state }: { state: RequestState }) => {
		return (
			<>
				{state.status === 'error' && (
					<div className={styles.error}>{state.error.message}</div>
				)}
			</>
		);
	};
	const StateDisplay = ({ state }: { state: RequestState }) => {
		return (
			<>
				{state.status === 'loading' && (
					<div className="is__pageloader">
						<figure>
							<LoaderBlue width={100} height={100} />
						</figure>
					</div>
				)}
			</>
		);
	};
	const ThanksDisplay = ({ state }: { state: RequestState }) => {
		return (
			<>
				{state.status === 'success' && (
					<div className={styles.success}>
						<h5 className="headingTagline">Submission Successful!</h5>
						<h2>Thank you!</h2>
						<p>Check your email. We’ll be in touch soon!</p>
					</div>
				)}
			</>
		);
	};
	const clearErrorState = () => {
		if (requestState.status === 'error') {
			setRequestState({ status: 'ready' });
		}
	};

	return (
		<>
			<div className={styles['formContainer']}>
				{!hideForm ? (
					<BaseForm
						formId={formProps.formId}
						onSubmit={handleSubmit(onSubmit)}
						onChange={clearErrorState}
					>
						{formProps.formData.map((field, index) => {
							const {
								name,
								labelText,
								inputType,
								placeholder,
								selectOptions,
								isRequired,
								validationErrorMessage,
							} = field;
							const validationRegex =
								'validationRegex' in field ? field.validationRegex : undefined;
							const id = field.name;

							const renderField = () => {
								const inputDataTestId = labelText
									.replace(/\s/g, '-')
									.replace(/[^a-zA-Z-]/g, '')
									.toLowerCase();

								const pattern = validationRegex
									? {
											value: RegExp(validationRegex),
											message: validationErrorMessage,
										}
									: undefined;
								switch (inputType) {
									case 'text':
										return (
											<>
												<input
													id={id}
													type="text"
													data-testid={inputDataTestId}
													{...register(name, {
														required: isRequired
															? isRequiredErrorMessage
															: false,
														pattern,
													})}
													placeholder={placeholder ?? ''}
												/>
												<span data-testid={`${inputDataTestId}-error`}>
													{errors[name] ? errors[name].message : ''}
												</span>
											</>
										);
									case 'email':
										return (
											<>
												<input
													type="text"
													id={id}
													data-testid={inputDataTestId}
													{...register(name, {
														required: isRequired
															? isRequiredErrorMessage
															: false,
														pattern,
													})}
													placeholder={placeholder ?? ''}
												/>
												<span data-testid={`${inputDataTestId}-error`}>
													{errors[name] ? errors[name].message : ''}
												</span>
											</>
										);
									case 'select':
										return (
											<>
												<select
													id={id}
													data-testid={inputDataTestId}
													{...register(name, {
														required: isRequired
															? isRequiredErrorMessage
															: false,
														pattern,
													})}
												>
													<option value="">{placeholder ?? 'Select'}</option>
													{selectOptions?.map((option, optionIndex) => (
														<option
															value={String(option).trim()}
															key={optionIndex}
														>
															{option}
														</option>
													))}
												</select>
												<span data-testid={`${inputDataTestId}-error`}>
													{errors[name] ? errors[name].message : ''}
												</span>
											</>
										);
									default:
										return null;
								}
							};

							return (
								<div
									key={index}
									className={styles['formField']}
									data-aos="fade-up"
								>
									{/* <label htmlFor={id}>{labelText}</label> */}
									{renderField()}
								</div>
							);
						})}

						<Button
							id="BecomeAnAgentForm-submit"
							buttonStyle="accent"
							type="submit"
							title="Submit"
							disabled={requestState.status === 'loading'}
							data-aos="fade-up"
						/>
						<ErrorDisplay state={requestState} />
					</BaseForm>
				) : (
					<StateDisplay state={requestState} />
				)}
			</div>
			<ThanksDisplay state={requestState} />
		</>
	);
}
